import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';

const NavigationContainer = styled(motion.nav)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: rgba(10, 10, 10, 0.95);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
`;

const NavContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 2rem;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Logo = styled(motion.div)`
  display: flex;
  align-items: center;
  font-size: 1.5rem;
  font-weight: 800;
  background: linear-gradient(135deg, #3b82f6, #06b6d4);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  cursor: pointer;
`;

const NavItems = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavItem = styled(motion.button)`
  background: none;
  border: none;
  color: ${props => props.active ? '#3b82f6' : '#94a3b8'};
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  position: relative;

  &:hover {
    color: #3b82f6;
    background: rgba(59, 130, 246, 0.1);
  }

  ${props => props.active && `
    &::after {
      content: '';
      position: absolute;
      bottom: -8px;
      left: 50%;
      transform: translateX(-50%);
      width: 4px;
      height: 4px;
      background: #3b82f6;
      border-radius: 50%;
    }
  `}
`;

const ProfileSelector = styled.select`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #ffffff;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.9rem;
  cursor: pointer;
  margin-left: 1rem;

  option {
    background: #1e293b;
    color: #ffffff;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: #ffffff;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;

  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileMenu = styled(motion.div)`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: rgba(10, 10, 10, 0.98);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding: 1rem 2rem;

  @media (min-width: 769px) {
    display: none;
  }
`;

const MobileNavItem = styled.button`
  display: block;
  width: 100%;
  background: none;
  border: none;
  color: ${props => props.active ? '#3b82f6' : '#94a3b8'};
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  padding: 1rem 0;
  text-align: left;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);

  &:hover {
    color: #3b82f6;
  }

  &:last-child {
    border-bottom: none;
  }
`;

function Navigation({ currentSection, userProfile, setUserProfile }) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const sections = [
    { id: 'hero', label: 'Home' },
    { id: 'executive-summary', label: 'Summary' },
    { id: 'calculator', label: 'Calculator' },
    { id: 'coupler-types', label: 'Products' },
    { id: 'performance-3d', label: '3D View' },
    { id: 'case-studies', label: 'Case Studies' },
    { id: 'contact', label: 'Contact' }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <NavigationContainer
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      style={{
        background: isScrolled 
          ? 'rgba(10, 10, 10, 0.98)' 
          : 'rgba(10, 10, 10, 0.80)'
      }}
    >
      <NavContent>
        <Logo
          whileHover={{ scale: 1.05 }}
          onClick={() => scrollToSection('hero')}
        >
          ADAPTEC BUILDING SOLUTIONS
        </Logo>

        <NavItems>
          {sections.map((section) => (
            <NavItem
              key={section.id}
              active={currentSection === section.id}
              onClick={() => scrollToSection(section.id)}
              whileHover={{ y: -2 }}
              whileTap={{ y: 0 }}
            >
              {section.label}
            </NavItem>
          ))}
        </NavItems>

        <div style={{ display: 'flex', alignItems: 'center' }}>
          <ProfileSelector
            value={userProfile}
            onChange={(e) => setUserProfile(e.target.value)}
          >
            <option value="engineer">Engineer</option>
            <option value="executive">Executive</option>
            <option value="contractor">Contractor</option>
            <option value="architect">Architect</option>
          </ProfileSelector>

          <MobileMenuButton
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            ☰
          </MobileMenuButton>
        </div>
      </NavContent>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <MobileMenu
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            {sections.map((section) => (
              <MobileNavItem
                key={section.id}
                active={currentSection === section.id}
                onClick={() => scrollToSection(section.id)}
              >
                {section.label}
              </MobileNavItem>
            ))}
          </MobileMenu>
        )}
      </AnimatePresence>
    </NavigationContainer>
  );
}

export default Navigation;